<script setup>
import Tag from './_tag.vue';
import { reactive, onMounted, onUnmounted, computed, watch, ref } from 'vue';

const emit = defineEmits(['update:includedTags', 'update:excludedTags', 'update:setDateRange', 'update:recipientCount']);

const props = defineProps({
    preincludedTags: {
        type: Array,
        default: () => []
    },
    preexcludedTags: {
        type: Array,
        default: () => []
    },
    timeRangeApplicable: {
        type: Boolean,
        required: false,
        default: false
    },
    dateRange: {
        type: Array,
        required: false,
    },
    recipientCounts: {
        type: Object,
        required: false
    },
    deliveryTypes: {
        type: Array,
        default: () => []
    }
})

const state = reactive({
    tags: [],
    loading: false,
    loadingTagsData: false,
    includedTags: props.preincludedTags,
    excludedTags: props.preexcludedTags,
})

const sortDropdownOpen = ref(false);
const selectedSort = ref(null);
const sortDropdownRef = ref(null);

const selectedRecipientCount = computed(() => {
    return props.recipientCounts.total;
})

const includedTagIds = computed(() => {
    return state.includedTags.map(tag => tag.id);
})
const excludedTagIds = computed(() => {
    return state.excludedTags.map(tag => tag.id);
})

const sortedTags = computed(() => {
    if (!selectedSort.value) {
        return state.tags;
    }

    const tags = [...state.tags];

    switch (selectedSort.value) {
        case 'most-used':
            return tags.sort((a, b) => (b.leads_count || 0) - (a.leads_count || 0));
        case 'newest':
            return tags.sort((a, b) => {
                const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                return dateB - dateA;
            });
        case 'oldest':
            return tags.sort((a, b) => {
                const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                return dateA - dateB;
            });
        default:
            return tags;
    }
})

const sortButtonText = computed(() => {
    if (!selectedSort.value) {
        return _mft('communication:tag.sort');
    }

    switch (selectedSort.value) {
        case 'most-used':
            return _mft('communication:tag.sort') + ': ' + _mft('communication:tag.sort.mostUsed');
        case 'newest':
            return _mft('communication:tag.sort') + ': ' + _mft('communication:tag.sort.newest');
        case 'oldest':
            return _mft('communication:tag.sort') + ': ' + _mft('communication:tag.sort.oldest');
        default:
            return _mft('communication:tag.sort');
    }
})

const setSort = (sortType) => {
    selectedSort.value = sortType;
    sortDropdownOpen.value = false;
}

const clearSort = () => {
    selectedSort.value = null;
    sortDropdownOpen.value = false;
}

watch(includedTagIds, (newValue, oldValue) => {
    loadRecipientCount()
})

watch(excludedTagIds, (newValue, oldValue) => {
    loadRecipientCount()
})

const loadRecipientCount = async () => {
    state.loadingTagsData = true;
    const res = await axios.get(route('crm.leadtags.count'), {
        params: {
            included_tags: includedTagIds.value,
            excluded_tags: excludedTagIds.value,
            once_assigned_in_time_tange_start: state.onceAssignedInTimeRangeStart,
            once_assigned_in_time_tange_end: state.onceAssignedInTimeRangeEnd,
            delivery_types: props.deliveryTypes ? [...props.deliveryTypes] : [],
        }
    });

    emit('update:recipientCount', res.data);
    state.loadingTagsData = false;
}

const loadTags = async () => {
    state.loading = true;
    const response = await axios.get(route('crm.leadtags.index'));
    state.tags = response.data.tags;
    state.loading = false;

    state.includedTags = props.preincludedTags;
    state.excludedTags = props.preexcludedTags;
}

const setDaterange = (data) => {
    state.onceAssignedInTimeRangeStart = data[0];
    state.onceAssignedInTimeRangeEnd = data[1];
    emit('update:setDateRange', data);
}

const presetDatetime = computed(() => {
    if (props.dateRange) {
        return props.dateRange;
    }
    const date = new Date();
    const minutes = date.getMinutes();
    const minutesToAdd = 15 - (minutes % 15);

    const roundedDate = new Date(date.getTime() + minutesToAdd * 60000);
    return roundedDate;
})

const include = ((tag) => {
    state.includedTags.push(tag)
    emit('update:includedTags', state.includedTags);
})
const unInclude = ((unincludedTag) => {
    state.includedTags = state.includedTags.filter(tag => tag.id !== unincludedTag.id);
    emit('update:includedTags', state.includedTags);
})

const exclude = ((tag) => {
    state.excludedTags.push(tag)
    emit('update:excludedTags', state.excludedTags);
})
const unExclude = ((unexcludedTag) => {
    state.excludedTags = state.excludedTags.filter(tag => tag.id !== unexcludedTag.id);
    emit('update:excludedTags', state.excludedTags);
})

const handleClickOutside = (e) => {
    if (sortDropdownRef.value && !sortDropdownRef.value.contains(e.target)) {
        sortDropdownOpen.value = false;
    }
};

onMounted(() => {
    loadTags();
    document.addEventListener('click', handleClickOutside);
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
})

watch(props, async (newValue, oldValue) => {
    state.includedTags = newValue.preincludedTags;
    state.excludedTags = newValue.preexcludedTags;
})

watch(() => props.deliveryTypes ? [...props.deliveryTypes] : [], () => {
    loadRecipientCount();
}, { deep: true, immediate: true })

</script>
<template>
    <div>
        <h2>
            <span>{{ _mft('communication:nRecipientsSelected', {count: selectedRecipientCount}) }}</span>
        </h2>
        <p
            v-if="timeRangeApplicable"
        >
            {{ _mft('communication:timeRangeApplicable.description') }}
        </p>
        <div class="space-y-4 lg:flex lg:space-x-8 lg:space-y-0">
            <div v-if="props.timeRangeApplicable">
                <input-label :label="_mft('communication:startEndDate')" />

                <input-datepicker
                    name="date"
                    :date="presetDatetime"
                    :minDate="null"
                    :rangeMode="true"
                    :showTime="false"
                    @update:date="setDaterange"
                    class="w-full mt-1"
                />
            </div>
            <div class="w-full">
                <div class="flex items-center justify-between mb-2">
                    <p>
                        {{ _mft('communication:selectRecipientsByTags') }}
                    </p>
                    <div class="relative" ref="sortDropdownRef">
                        <button
                            @click.stop="sortDropdownOpen = !sortDropdownOpen"
                            class="myflow-basic-button text-xs"
                            type="button"
                        >
                            {{ sortButtonText }}
                            <i class="fa-light fa-chevron-down ml-1" :class="{ 'fa-chevron-up': sortDropdownOpen }"></i>
                        </button>
                        <div
                            v-if="sortDropdownOpen"
                            class="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                            @click.stop
                        >
                            <div class="py-1">
                                <button
                                    @click="setSort('most-used')"
                                    class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                    :class="{ 'bg-gray-100 font-semibold': selectedSort === 'most-used' }"
                                >
                                    {{ _mft('communication:tag.sort.mostUsed') }}
                                </button>
                                <button
                                    @click="setSort('newest')"
                                    class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                    :class="{ 'bg-gray-100 font-semibold': selectedSort === 'newest' }"
                                >
                                    {{ _mft('communication:tag.sort.newest') }}
                                </button>
                                <button
                                    @click="setSort('oldest')"
                                    class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                    :class="{ 'bg-gray-100 font-semibold': selectedSort === 'oldest' }"
                                >
                                    {{ _mft('communication:tag.sort.oldest') }}
                                </button>
                                <button
                                    v-if="selectedSort"
                                    @click="clearSort"
                                    class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-500 border-t border-gray-200 mt-1"
                                >
                                    {{ _mft('communication:tag.sort.clear') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <p v-if="state.loading" class="py-2 italic">{{ _mft('communication:loadingTags') }}</p>
                <ul class="divide-y">
                    <li
                        v-if="!state.loading && sortedTags.length === 0"
                        class="py-2 italic text-gray-500"
                    >
                        {{ _mft('communication:noTagsFound') }}
                    </li>
                    <li
                        v-for="tag in sortedTags"
                        :key="tag.id"
                        class="items-center justify-between py-2 md:py-1 md:flex md:space-x-4"
                    >
                        <Tag
                            :tag="tag"
                            :isIncluded="includedTagIds.includes(tag.id)"
                            :isExcluded="excludedTagIds.includes(tag.id)"
                            :loading="state.loadingTagsData"
                            @include="include"
                            @uninclude="unInclude"
                            @exclude="exclude"
                            @unexclude="unExclude"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
